<template>
  <MentionCard
    ref="card"
    v-intersection="intersectionConfig"
    :action-sheet-component="actionSheetComponent"
    :always-show-keywords="alwaysShowKeywords"
    card-title-class="tw:hover:underline"
    :class="mentionCardClass"
    :logo="mention.source ? mention.source.logo_url : null"
    :mention="mention"
    :mentions="mentions"
    :no-highlighting="noHighlighting"
    :options="options"
    :selected="selected"
    :show-checkbox="showCheckbox"
    :stream="stream"
    :title="mentionTitle"
    @click="mentionClicked($event)"
    @mention-removed="removeMention"
    @mention-selected="$emit('mention-selected', $event)"
    @translate="$emit('translate', $event)"
  >
    <template #before>
      <div
        v-if="mention.on_front_page && mention.og_image"
        class="article-thumbnail"
        :style="{ backgroundImage: `url('${mention.og_image}')` }"
      />
    </template>

    <template #header>
      <SyndicationsPopper
        v-if="mention?.syndications?.length"
        ref="syndicationsPopperRef"
        v-slot="{ toggleOpen }"
        :mention="mention"
        :parent-el-ref="card?.$el"
        :stream="stream"
        @source-name-click="(source) => handleOpenOutletSidebar(source)"
        @syndication-clicked="mentionClicked($event)"
      >
        <BaseButton
          new-design
          plain
          @click.stop.prevent="toggleOpen"
        >
          <BaseLineClamp
            class="tw:break-all tw:text-sm tw:text-denim-900"
            :lines="1"
          >
            <span
              v-if="isOutletEnabled(mention.source)"
              class="tw:cursor-pointer tw:hover:underline tw:hover:font-normal tw:hover:text-blue-500"
              @click.stop.prevent="handleOpenOutletSidebar(mention.source)"
            >
              {{ mention.source.name }}
            </span>
            <span
              v-else
              @click.stop.prevent
            >
              {{ mention.source.name }}
            </span>

            <template #after="{ clamped }">
              <BaseStreemTooltip v-if="clamped"
                >{{ mention.source.name }}
              </BaseStreemTooltip>
            </template>
          </BaseLineClamp>

          <span
            class="tw:text-sm tw:text-denim-900 tw:cursor-pointer tw:hover:underline tw:hover:font-normal tw:hover:text-blue-500"
          >
            +{{ mention.syndications.length }}
          </span>
        </BaseButton>
      </SyndicationsPopper>
      <BaseLineClamp
        v-else
        tag="span"
      >
        <span
          v-if="isOutletEnabled(mention.source)"
          class="tw:cursor-pointer tw:hover:underline tw:hover:font-normal tw:hover:text-blue-500"
          @click.stop.prevent="handleOpenOutletSidebar(mention.source)"
        >
          {{ mention.source.name }}
        </span>
        <span
          v-else
          @click.stop.prevent
        >
          {{ mention.source.name }}
        </span>
        <template #after="{ clamped }">
          <BaseStreemTooltip v-if="clamped"
            >{{ mention.source.name }}
          </BaseStreemTooltip>
        </template>
      </BaseLineClamp>
      <span class="softer tw:text-sm tw:leading-4">
        <TimeAgo
          :date="mention.timestamp"
          :timezone="mention.source.time_zone"
        />
        <AuthorWidget
          v-if="mention.author"
          class="tw:hover:text-blue-500 tw:hover:underline"
          disable-author-click-default
          :disable-click="options.disableAuthorClick"
          :mention="mention"
          @author-click="handleOpenOutreachContactSidebar"
        />
        <span>
          {{
            $t("online_mention_card.word_count", {
              wordCount: mention.word_count,
            })
          }}
        </span>
      </span>
    </template>

    <slot />

    <template #list-body-append>
      <div @click.stop>
        <SocialAmplifyWidget
          v-if="card"
          dense
          is-list-view
          :is-parent-visible="isVisible"
          :mention="mention"
          :root-element="card.$el"
          scroll-into-view
          @click="onClick"
        />
      </div>
    </template>

    <template #afterBody>
      <MentionCardInsights
        v-if="!hideMentionCardMetrics"
        :mention-title="mentionTitle"
        :visible-widgets="visibleWidgets"
        :widget-keys="[sentimentWidgetKey, socialAmplifyWidgetKey]"
        @click.stop.prevent
      >
        <template #components>
          <PotentialAudienceReachWidget
            dense
            :mention="mention"
          />

          <AdvertisingValueWidget
            dense
            :mention="mention"
          />

          <SentimentWidget
            :key="sentimentWidgetKey"
            dense
            :mention="mention"
            show-media-item-button
            :stream="stream"
          />

          <ReactScoreWidget
            dense
            :is-parent-visible="isVisible"
            :mention="mention"
          />

          <ImpactScoreWidget
            dense
            :impact-score="mention.impact_score"
          />

          <SocialAmplifyWidget
            v-if="card"
            :key="socialAmplifyWidgetKey"
            dense
            :is-parent-visible="isVisible"
            :mention="mention"
            :root-element="card.$el"
            scroll-into-view
            @click="onClick"
            @update-social-amplify-widget-key="updateSocialAmplifyWidgetKey"
          />

          <DomainAuthorityWidget
            dense
            :mention="mention"
          />
        </template>
      </MentionCardInsights>

      <MentionTranslationLanguage
        v-if="showTranslationLanguage"
        :mention="mention"
      />
    </template>

    <template #after>
      <!-- Related Print Article -->
      <div v-if="canShowRelatedArticle">
        <RelatedMentionCard
          :action-sheet-component="actionSheetComponent"
          :mention="mention.related_article"
          :stream="stream"
          @mention:click="mentionClicked($event)"
          @syndication:click="mentionClicked($event)"
        >
          {{ $t("online_mention_card.view_print_version") }}
        </RelatedMentionCard>
      </div>
    </template>
  </MentionCard>
</template>

<script>
import { storeToRefs } from "pinia";
import { ref } from "vue";

import AdvertisingValueWidget from "shared/components/AdvertisingValueWidget.vue";
import AuthorWidget from "shared/components/AuthorWidget.vue";
import {
  BaseButton,
  BaseLineClamp,
  BaseStreemTooltip,
} from "shared/components/base";
import TimeAgo from "shared/components/core/various/TimeAgo.vue";
import DomainAuthorityWidget from "shared/components/DomainAuthorityWidget.vue";
import ImpactScoreWidget from "shared/components/ImpactScoreWidget.vue";
import { MentionCard, props } from "shared/components/MentionCard";
import MentionCardInsights from "shared/components/MentionCardInsights.vue";
import MentionTranslationLanguage from "shared/components/MentionTranslationLanguage.vue";
import PotentialAudienceReachWidget from "shared/components/PotentialAudienceReachWidget.vue";
import ReactScoreWidget from "shared/components/ReactScoreWidget.vue";
import { RelatedMentionCard } from "shared/components/RelatedMentionCard";
import SentimentWidget from "shared/components/SentimentWidget.vue";
import SocialAmplifyWidget from "shared/components/SocialAmplifyWidget.vue";
import { SyndicationsPopper } from "shared/components/SyndicationsPopper";
import useMention from "shared/composables/useMention";
import useSidebarOnMentionCard from "shared/composables/useSidebarOnMentionCard";
import useSocialAmplify from "shared/composables/useSocialAmplify";
import { useUniversalSearchStore } from "shared/stores/universalSearch";
import { useUserStore } from "shared/stores/user";

export default {
  name: "OnlineMentionCard",
  components: {
    AdvertisingValueWidget,
    AuthorWidget,
    BaseButton,
    DomainAuthorityWidget,
    BaseLineClamp,
    MentionCard,
    MentionCardInsights,
    MentionTranslationLanguage,
    SocialAmplifyWidget,
    PotentialAudienceReachWidget,
    ReactScoreWidget,
    ImpactScoreWidget,
    RelatedMentionCard,
    SentimentWidget,
    BaseStreemTooltip,
    SyndicationsPopper,
    TimeAgo,
  },
  props: {
    ...props,
    hideMentionCardMetrics: {
      type: Boolean,
    },
  },
  emits: [
    "click",
    "mention-selected",
    "mention-removed",
    "translate",
    "contact-clicked",
  ],
  setup(mentionProps, context) {
    const card = ref();
    const syndicationsPopperRef = ref();

    const { isMentionNotClickable, mentionClicked } = useMention(
      mentionProps,
      context
    );

    const universalSearchStore = useUniversalSearchStore();
    const { searchOpen } = storeToRefs(universalSearchStore);

    const userStore = useUserStore();

    const { adminUserEnabled, currentUser, isAdminUser } =
      storeToRefs(userStore);

    const { hasAmplify } = useSocialAmplify({
      mention: mentionProps.mention,
    });

    const {
      isOutletEnabled,
      handleOpenOutletSidebar,
      handleOpenOutreachContactSidebar,
    } = useSidebarOnMentionCard({
      emit: context.emit,
    });

    return {
      card,
      syndicationsPopperRef,
      isMentionNotClickable,
      mentionClicked,
      searchOpen,
      adminUserEnabled,
      currentUser,
      isAdminUser,
      hasAmplify,
      isOutletEnabled,
      handleOpenOutletSidebar,
      handleOpenOutreachContactSidebar,
    };
  },
  data() {
    return {
      isVisible: false,
      hasRecorded: false,
      intersectionConfig: {
        handler: this.onIntersection,
        cfg: {
          threshold: [0, 0.25, 0.5, 0.75, 1],
        },
      },
      sentimentValue: null,
      socialAmplifyValue: 0,
    };
  },
  computed: {
    sentimentWidgetKey() {
      return `sentiment-widget-${this.mention.id}-${this.sentimentValue}`;
    },
    socialAmplifyWidgetKey() {
      return `social-amplify-widget-${this.mention.id}-${this.socialAmplifyValue}`;
    },
    canShowReactScore() {
      return (
        this.mention.factmata_enrichment &&
        this.$features.has("has_react_score")
      );
    },
    visibleWidgets() {
      const widgets = ["SentimentWidget"];

      if (this.card && this.hasAmplify) {
        widgets.push("SocialAmplifyWidget");
      }

      if (this.mention.source?.domain_authority)
        widgets.push("DomainAuthorityWidget");

      if (this.mention.audience) widgets.push("PotentialAudienceReachWidget");

      if (
        this.mention.advertising_value &&
        this.$features.has("advertising_value_mentions")
      ) {
        widgets.push("AdvertisingValueWidget");
      }

      if (this.canShowReactScore) widgets.push("ReactScoreWidget");

      if (this.mention.impact_score?.grade) {
        widgets.push("ImpactScoreWidget");
      }

      return widgets;
    },
    canShowRelatedArticle() {
      return (
        this.$features.has("view_paper_articles") &&
        this.mention.related_article &&
        (!this.searchOpen || this.adminUserEnabled)
      );
    },
    mentionTitle() {
      return this.mention.translated_title || this.mention.title;
    },
    showTranslationLanguage() {
      return this.mention.original_language !== "English";
    },
    mentionCardClass() {
      if (this.isMentionNotClickable(this.mention)) return null;

      return "pointer";
    },
  },
  watch: {
    "mention.sentiment_ratings": {
      deep: true,
      handler(options) {
        if (options?.length) {
          this.sentimentValue = options[0].sentiment;
        }
      },
    },
  },
  mounted() {
    this.mention.sentiment_ratings ??= [];
  },
  methods: {
    onIntersection({ isIntersecting, intersectionRatio }) {
      this.isVisible = isIntersecting;

      this.recordView();

      if (intersectionRatio < 0.75) {
        this.isVisible = false;
      }
    },
    updateSocialAmplifyWidgetKey(amplifyVolume) {
      this.socialAmplifyValue = amplifyVolume;
    },
    onClick() {
      this.$refs.card.$el.scrollIntoView({ behaviour: "smooth" });
    },
    recordView() {
      if (this.isVisible && this.$route.name !== "curated-email-report") {
        setTimeout(() => {
          if (this.isVisible && !this.isAdminUser && !this.hasRecorded) {
            this.hasRecorded = true;

            this.$track("Saw Online Article", {
              userId: this.currentUser.id,
              articleId: this.mention.id,
              sourceId: this.mention.source.id,
            });
          }
        }, 300);
      }
    },
    removeMention(mention) {
      this.$emit("mention-removed", mention);
    },
  },
};
</script>

<style lang="scss" scoped>
.article-thumbnail {
  width: 100%;
  background-size: cover;
  background-position: 50% 30%;
  height: 120px;
}

.helper-text {
  margin-top: 9px;
}

.source-title :deep(.ellipsis) {
  display: inline-block;
  max-width: 210px;
  white-space: normal;
  vertical-align: bottom;
}

.after-body {
  display: flex;
  gap: 12px;
  align-items: center;
}
</style>
